import React, { useEffect, useRef, useState } from "react";


/**
 * Props:
 * - text: full HTML string
 * - collapsedHeight: height in px for collapsed state
 *
 * Important:
 * 
 * -> Put your strongest 2-4 line summary as the FIRST paragraph inside `text`.
 * That way users see a good preview, and full content is still present in HTML. 
 * 
 * -> This is better because having full html will give me SEO benefits, 
 * while showing only 2-3 lines with last line text slightly faded
 * which  will give better UI to users initially. 
 */
function ReadMoreButtonHeight({ text, collapsedHeight = 200 }) {
  const [expanded, setExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);

  const wrapperRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    function checkOverflow() {
      if (!contentRef.current) return;
      setShowButton(contentRef.current.scrollHeight > collapsedHeight + 5);
    }

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [text, collapsedHeight]);

  if (!text || text.length === 0) {
    return <span></span>;
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div
        className="relative overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: expanded ? "none" : `${collapsedHeight}px`,
        }}
      >
        <div
          ref={contentRef}
          className="prose prose-slate max-w-none text-base leading-7 text-slate-600"
          dangerouslySetInnerHTML={{ __html: text }}
        />

        {!expanded && showButton && (
          <div className="pointer-events-none absolute bottom-0 left-0 h-16 w-full bg-gradient-to-t from-white via-white/90 to-transparent" />
        )}
      </div>

      {showButton && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className=" inline-flex items-center gap-2 rounded-full bg-orange-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 hover:shadow-md"
        >
          {expanded ? "Show Less" : "Read More"}
          <span className="text-lg leading-none">
            {expanded ? "↑" : "→"}
          </span>
        </button>
      )}
    </div>
  );
}

function ReadMoreButtonHeightOld1({ text, collapsedHeight = 200 }) {
  const [expanded, setExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);

  const wrapperRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    function checkOverflow() {
      if (!contentRef.current) return;
      setShowButton(contentRef.current.scrollHeight > collapsedHeight + 5);
    }

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [text, collapsedHeight]);

  if (!text || text.length === 0) {
    return <span></span>;
  }

  const handleToggle = () => {
    if (expanded && wrapperRef.current) {
      wrapperRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setExpanded(!expanded);
  };

  return (
    <div ref={wrapperRef} className="text-gray-700 [&_a]:underline">
      <div
        className="relative overflow-hidden transition-all duration-300"
        style={{
          maxHeight: expanded ? `${contentRef.current?.scrollHeight 
                      || 9999}px` : `${collapsedHeight}px`,
        }}
      >
        <div
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: text }}
        />

        {!expanded && showButton && (
          <div className="pointer-events-none absolute bottom-0 left-0 
          right-0 h-12 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>

      {showButton && (
        <div className="mt-3">
          <button
            onClick={handleToggle}
            className="rounded-3xl bg-orange-600 px-4 py-1 text-white"
          >
            {expanded ? "Read Less" : "Read More"}
          </button>
        </div>
      )}
    </div>
  );
}

export {  ReadMoreButtonHeight};